import { hasPurchasableStockForListing } from './listingStock';

const TUTULIST_USER_ID = '62f6cbdb-5201-44ab-9b62-b2c234b566ec';
const TUTULIST_LOWERCASE = 'tutulist';
const ENCORE_PUBLIC_DATA_KEY = 'tutulistEncore';
const DEBUG_FLAG_VALUE = 'true';
const PRIVATE_LISTING_FLAG_KEY = 'privateListing';

const normalizeId = value =>
  value == null
    ? ''
    : typeof value === 'object'
    ? value?.uuid || value?.id || value?.value || ''
    : `${value}`;

const normalizeHandle = value =>
  (value || '')
    .toString()
    .trim()
    .toLowerCase();

const isTruthy = value =>
  Array.isArray(value)
    ? value.some(item => isTruthy(item))
    : value === true ||
      value === 1 ||
      ['true', '1', 'yes'].includes(typeof value === 'string' ? value.trim().toLowerCase() : null);

const getListingId = listing => listing?.id?.uuid || listing?.id || null;

const getListingTitle = listing => listing?.attributes?.title || listing?.title || null;

const getListingSlug = listing => listing?.attributes?.publicData?.slug || listing?.slug || null;

const getListingPublicData = listing =>
  listing?.attributes?.publicData || listing?.publicData || {};

const getAuthorFromListing = listing => listing?.author || listing?.relationships?.author?.data;

const getSellerId = seller => seller?.id?.uuid || seller?.id || null;

const getSellerUsername = seller =>
  seller?.attributes?.profile?.publicData?.username || seller?.username || null;

const getSellerSlug = seller =>
  seller?.attributes?.profile?.publicData?.slug || seller?.slug || null;

const getRuntimeEnv = key => {
  return typeof process !== 'undefined' && process.env ? process.env[key] : undefined;
};

const shouldDebug = () => getRuntimeEnv('DEBUG_ENCORE_HIDE') === DEBUG_FLAG_VALUE;
const shouldDebugPrivate = () => getRuntimeEnv('DEBUG_PRIVATE_LISTINGS') === DEBUG_FLAG_VALUE;

const logPrivateMatch = (listing, rule, extras = {}) => {
  if (!shouldDebugPrivate()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[private-listings] matched listing', {
    id: getListingId(listing),
    title: getListingTitle(listing),
    slug: getListingSlug(listing),
    rule,
    ...extras,
  });
};

const logEncoreFiltered = (listing, rule) => {
  if (!shouldDebug()) {
    return;
  }

  // eslint-disable-next-line no-console
  console.log('[encore-hide] filtered listing', {
    id: getListingId(listing),
    title: getListingTitle(listing),
    slug: getListingSlug(listing),
    rule,
  });
};

const isTutulistHandle = value => normalizeHandle(value) === TUTULIST_LOWERCASE;

const isTutulistSeller = seller => {
  if (!seller) return false;

  const id = getSellerId(seller);
  if (id && id === TUTULIST_USER_ID) return true;

  const username = getSellerUsername(seller);
  const slug = getSellerSlug(seller);

  return isTutulistHandle(username) || isTutulistHandle(slug);
};

const isEncoreCollectionListing = listing => {
  if (!listing) return false;

  const publicData = listing?.attributes?.publicData;
  if (isTruthy(publicData?.[ENCORE_PUBLIC_DATA_KEY])) {
    logEncoreFiltered(listing, 'publicData.tutulistEncore');
    return true;
  }

  const author = getAuthorFromListing(listing);
  if (isTutulistSeller(author)) {
    logEncoreFiltered(listing, 'author.tutulist');
    return true;
  }

  return false;
};

const isPrivateListing = listing => {
  if (!listing) return false;

  const publicData = getListingPublicData(listing);
  const privateListing = publicData?.[PRIVATE_LISTING_FLAG_KEY];
  if (isTruthy(privateListing)) {
    logPrivateMatch(listing, 'publicData.privateListing');
    return true;
  }

  return false;
};

const isVacationModeListing = listing => {
  if (!listing) return false;

  const publicData = getListingPublicData(listing);
  // sellerVacationMode is a response-only value resolved through our server with
  // the Integration API. The legacy vacationMode field is a stale listing copy and
  // must not be used as a source of truth.
  return isTruthy(publicData?.sellerVacationMode);
};

const getListingAuthorId = listing => {
  const author = getAuthorFromListing(listing);
  return normalizeId(author?.id || author);
};

const isOwnerViewer = (listing, viewerUserId) => {
  const listingAuthorId = getListingAuthorId(listing);
  const viewerId = normalizeId(viewerUserId);
  return !!listingAuthorId && !!viewerId && listingAuthorId === viewerId;
};

const isAdminViewer = isAdmin => isTruthy(isAdmin);

const canViewerSeeOutOfStockListing = (listing, viewerUserId, isAdmin = false) =>
  isOwnerViewer(listing, viewerUserId) || isAdminViewer(isAdmin);

const hasBuyerVisibleStock = (listing, viewerUserId, isAdmin = false) =>
  canViewerSeeOutOfStockListing(listing, viewerUserId, isAdmin) ||
  hasPurchasableStockForListing(listing);

const isNonPublicListingState = listing => {
  const state = listing?.attributes?.state;
  const deleted = listing?.attributes?.deleted;

  if (deleted === true) {
    return true;
  }

  if (typeof state === 'string' && state.length > 0 && state !== 'published') {
    return true;
  }

  return isVacationModeListing(listing);
};

const canViewListing = (listing, viewerUserId, isAdmin = false) => {
  if (!listing) return false;

  if (!isPrivateListing(listing)) {
    return true;
  }

  const ownerViewer = isOwnerViewer(listing, viewerUserId);
  const adminViewer = isAdminViewer(isAdmin);
  const canView = ownerViewer || adminViewer;

  if (!canView) {
    logPrivateMatch(listing, 'blocked-viewer', {
      viewerUserId: normalizeId(viewerUserId) || null,
    });
  }

  return canView;
};

const canTransactOnListing = (listing, viewerUserId, isAdmin = false) => {
  if (!listing) return false;

  if (!canViewListing(listing, viewerUserId, isAdmin)) {
    return false;
  }

  return (
    !isNonPublicListingState(listing) &&
    !isEncoreCollectionListing(listing) &&
    hasBuyerVisibleStock(listing, viewerUserId, isAdmin)
  );
};

const canDirectlyViewListing = (listing, viewerUserId, isAdmin = false) => {
  if (!listing) return false;

  if (!isPrivateListing(listing)) {
    return true;
  }

  if (isOwnerViewer(listing, viewerUserId) || isAdminViewer(isAdmin)) {
    return true;
  }

  return !isNonPublicListingState(listing);
};

const canDirectlyTransactOnListing = (listing, viewerUserId, isAdmin = false) => {
  if (!canDirectlyViewListing(listing, viewerUserId, isAdmin)) {
    return false;
  }

  return (
    !isNonPublicListingState(listing) &&
    !isEncoreCollectionListing(listing) &&
    hasBuyerVisibleStock(listing, viewerUserId, isAdmin)
  );
};

const canCheckoutOnListing = (listing, viewerUserId, isAdmin = false) => {
  if (!listing) return false;

  if (!canViewListing(listing, viewerUserId, isAdmin)) {
    return false;
  }

  const state = listing?.attributes?.state;
  const deleted = listing?.attributes?.deleted;
  const isAllowedCheckoutState = !state || state === 'published' || state === 'closed';

  return (
    deleted !== true &&
    !isVacationModeListing(listing) &&
    isAllowedCheckoutState &&
    !isEncoreCollectionListing(listing) &&
    hasBuyerVisibleStock(listing, viewerUserId, isAdmin)
  );
};

const isListingPubliclyVisible = listing => {
  if (!listing) return false;

  if (!canViewListing(listing, null, false)) {
    return false;
  }

  return !isNonPublicListingState(listing) && !isEncoreCollectionListing(listing);
};

const filterPubliclyVisibleListings = listings =>
  Array.isArray(listings) ? listings.filter(isListingPubliclyVisible) : [];

const filterPublicListings = (listings, viewerUserId, isAdmin = false) =>
  Array.isArray(listings)
    ? listings.filter(listing => canTransactOnListing(listing, viewerUserId, isAdmin))
    : [];

export {
  ENCORE_PUBLIC_DATA_KEY,
  PRIVATE_LISTING_FLAG_KEY,
  isPrivateListing,
  isVacationModeListing,
  canViewListing,
  canTransactOnListing,
  canDirectlyViewListing,
  canDirectlyTransactOnListing,
  canCheckoutOnListing,
  filterPublicListings,
  isEncoreCollectionListing,
  isListingPubliclyVisible,
  filterPubliclyVisibleListings,
};
